import "./sidebar.css";
import {
    LineStyle,
    Timeline,
    PermIdentity,
    Storefront,
    AttachMoney,
    Add,
    StoreOutlined,
    QueryBuilder,
    Code,
    Redeem,
    Assessment,
    Flag,
    ChevronLeft,
    ChevronRight,
    ExitToApp,
} from "@material-ui/icons";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { logout } from "../../redux/userRedux";
import { checkUserStatus } from "../../redux/apiCalls";

export default function Sidebar() {
    // Set collapsed state based on screen size - collapsed by default for mobile and tablet
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return window.innerWidth <= 1024;
    });
    const dispatch = useDispatch();

    const currentUser = useSelector((state) => state.user.currentUser);

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin : null
    );

    const admin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isAdmin : null
    );

    const outright = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "outright"
            : null
    );

    const marketing = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "marketing"
            : null
    );

    const accountant = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "accountant"
            : null
    );

    const hq = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.email === "admin@ssrmotorsport.com"
            : null
    );

    // Handle window resize to collapse/expand sidebar on screen size change
    useEffect(() => {
        const handleResize = () => {
            const shouldCollapse = window.innerWidth <= 1024;
            setIsCollapsed(shouldCollapse);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Check user status from backend once on mount
    useEffect(() => {
        if (!currentUser || !currentUser._id) return;

        const checkStatus = async () => {
            const userData = await checkUserStatus(currentUser._id);
            if (userData && userData.status === "inactive") {
                dispatch(logout());
                alert("Your account has been deactivated. You have been logged out.");
                window.location.href = '/';
            }
        };

        checkStatus();
    }, [currentUser, dispatch]);

    // Get user initials for avatar
    const getUserInitials = (name) => {
        if (!name) return "U";
        const nameParts = name.split(" ");
        if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    };

    // Get user role display
    const getUserRole = () => {
        if (!currentUser) return "User";
        if (currentUser.isSuperAdmin) return "Superadmin";
        if (currentUser.isAdmin) return "Admin";
        if (currentUser.type === "accountant") return "Accountant";
        if (currentUser.type === "outright") return "Outright";
        if (currentUser.type === "marketing") return "Marketing";
        if (currentUser.type === "staff") return "Staff";
        return "User";
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleLogout = () => {
        dispatch(logout());
        // Optionally redirect to login page or clear any other state
        window.location.href = '/';
    };

    if (!hq) {
        return (
            <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebarWrapper">
                    <div className="sidebarHeader">
                        <div className="logoContainer">
                            {!isCollapsed && (
                                <Link to="/" style={{ textDecoration: 'none' }}>
                                    <img 
                                        src="https://ik.imagekit.io/pjakhdl7q/Air%20Vitamin/air-logo_xVbwmEdOS.webp?updatedAt=1761103246859" 
                                        alt="Air Logo" 
                                        className="sidebarLogo"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </Link>
                            )}
                            <button className="toggleButton" onClick={toggleSidebar}>
                                {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                            </button>
                        </div>
                        {!isCollapsed && currentUser && (
                            <div className="userInfo">
                                <div className="userAvatar">{getUserInitials(currentUser.name || currentUser.username)}</div>
                                <div className="userDetails">
                                    <div className="userName">{currentUser.name || currentUser.username}</div>
                                    <div className="userRole">{getUserRole()}</div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="sidebarMenu">
                        <h3 className="sidebarTitle">DASHBOARD</h3>
                        <ul className="sidebarList">
                            <Link
                                style={{
                                    textDecoration: "none",
                                    color: "black",
                                }}
                                to="/"
                                className="link"
                            >
                                <li className="sidebarListItem active">
                                    <LineStyle className="sidebarIcon" />
                                    {!isCollapsed && "Home"}
                                </li>
                            </Link>
                            {!accountant && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/pos"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Timeline className="sidebarIcon" />
                                            {!isCollapsed && "POS"}
                                        </li>
                                    </Link>

                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/orders"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <AttachMoney className="sidebarIcon" />
                                            {!isCollapsed && "Orders"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>

                    <div className="sidebarMenu">
                        {!isCollapsed && <h3 className="sidebarTitle">USERS</h3>}
                        <ul className="sidebarList">
                            {(superadmin || marketing || outright) && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/users"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <PermIdentity className="sidebarIcon" />
                                            {!isCollapsed && (outright ? "Dealers" : "Users")}
                                        </li>
                                    </Link>
                                </>
                            )}
                            {!accountant && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/newuser"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Add className="sidebarIcon" />
                                            {!isCollapsed && "New User"}
                                        </li>
                                    </Link>
                                    {superadmin && (
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/newdealer"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Add className="sidebarIcon" />
                                            {!isCollapsed && "New Dealer"}
                                        </li>
                                    </Link>
                                    )}
                                    {(superadmin || outright) && (
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/newstaff"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Add className="sidebarIcon" />
                                            {!isCollapsed && "New Subdealer"}
                                        </li>
                                    </Link>
                                    )}
                                    {(superadmin) && (
                                          <Link
                                          style={{
                                              textDecoration: "none",
                                              color: "black",
                                          }}
                                          to="/outlets"
                                          className="link"
                                      >
                                          <li className="sidebarListItem">
                                              <StoreOutlined className="sidebarIcon" />
                                              {!isCollapsed && "Admin Accounts"}
                                          </li>
                                      </Link>
                                      )}
                                </>
                            )}

                            <Link
                                style={{
                                    textDecoration: "none",
                                    color: "black",
                                }}
                                to="/commissions"
                                className="link"
                            >
                                <li className="sidebarListItem">
                                    <Redeem className="sidebarIcon" />
                                    {!isCollapsed && "Commission"}
                                </li>
                            </Link>
                        </ul>
                    </div>
                    <div className="sidebarMenu">
                {(superadmin || outright) && !isCollapsed && (
                    <h3 className="sidebarTitle">REPORTS</h3>)}
                    <ul className="sidebarList">
                        {superadmin && (
                            <>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/productreport"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <Assessment className="sidebarIcon" />
                                        {!isCollapsed && "Report Analysis"}
                                    </li>
                                </Link>
                            </>
                        )}

                        {(superadmin || outright) && (
                            <>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/salesreport"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <Flag className="sidebarIcon" />
                                        {!isCollapsed && "Outlet Report"}
                                    </li>
                                </Link>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/report"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <QueryBuilder className="sidebarIcon" />
                                        {!isCollapsed && "Monthly Report"}
                                    </li>
                                </Link>
                        
                            </>
                        )}
                    </ul>
                </div>
                    <div className="sidebarMenu">
                        {!isCollapsed && <h3 className="sidebarTitle">DEALER CODE</h3>}
                        <ul className="sidebarList">
                            <>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/affiliates"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <Code className="sidebarIcon" />
                                        {!isCollapsed && "Dealer Code"}
                                    </li>
                                </Link>
                                {(superadmin || outright) && (
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/newaffiliate"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <Add className="sidebarIcon" />
                                        {!isCollapsed && "New Dealer Code"}
                                    </li>
                                </Link>
        )}
                            </>
                        </ul>
                    </div>

                    <div className="sidebarMenu">
                        {(superadmin || admin || marketing) && !isCollapsed && (
                            <h3 className="sidebarTitle">PRODUCTS</h3>
                        )}
                        <ul className="sidebarList">
                            {(superadmin || admin || marketing) && (
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/products"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <Storefront className="sidebarIcon" />
                                        {!isCollapsed && "Products"}
                                    </li>
                                </Link>
                            )}
                            {(superadmin || marketing) && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/newproduct"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Add className="sidebarIcon" />
                                            {!isCollapsed && "New Product"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>
                    
                    {/* Logout Button */}
                    <div className="logoutSection">
                        <button className="logoutButton" onClick={handleLogout}>
                            <ExitToApp className="sidebarIcon" />
                            {!isCollapsed && "Logout"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        hq && (
            <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebarWrapper">
                    <div className="sidebarHeader">
                        <div className="logoContainer">
                            {!isCollapsed && (
                                <Link to="/" style={{ textDecoration: 'none' }}>
                                    <img 
                                        src="https://ik.imagekit.io/pjakhdl7q/Air%20Vitamin/air-logo_xVbwmEdOS.webp?updatedAt=1761103246859" 
                                        alt="Air Logo" 
                                        className="sidebarLogo"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </Link>
                            )}
                            <button className="toggleButton" onClick={toggleSidebar}>
                                {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                            </button>
                        </div>
                        {!isCollapsed && currentUser && (
                            <div className="userInfo">
                                <div className="userAvatar">{getUserInitials(currentUser.name || currentUser.username)}</div>
                                <div className="userDetails">
                                    <div className="userName">{currentUser.name || currentUser.username}</div>
                                    <div className="userRole">{getUserRole()}</div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="sidebarMenu">
                        <h3 className="sidebarTitle">DASHBOARD</h3>
                        <ul className="sidebarList">
                            <Link
                                style={{
                                    textDecoration: "none",
                                    color: "black",
                                }}
                                to="/"
                                className="link"
                            >
                                <li className="sidebarListItem active">
                                    <LineStyle className="sidebarIcon" />
                                    {!isCollapsed && "Home"}
                                </li>
                            </Link>
                            {!accountant && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/pos"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Timeline className="sidebarIcon" />
                                            {!isCollapsed && "POS"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>
                    <div className="sidebarMenu">
                        {!isCollapsed && <h3 className="sidebarTitle">USERS</h3>}
                        <ul className="sidebarList">
                            {(superadmin || marketing || outright) && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/users"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <PermIdentity className="sidebarIcon" />
                                            {!isCollapsed && "Users"}
                                        </li>
                                    </Link>
                                </>
                            )}
                            {!accountant && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/newuser"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Add className="sidebarIcon" />
                                            {!isCollapsed && "New User"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>

                    <div className="sidebarMenu">
                        {(superadmin || admin || marketing) && !isCollapsed && (
                            <h3 className="sidebarTitle">PRODUCTS</h3>
                        )}
                        <ul className="sidebarList">
                            {(superadmin || admin || marketing) && (
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "black",
                                    }}
                                    to="/products"
                                    className="link"
                                >
                                    <li className="sidebarListItem">
                                        <Storefront className="sidebarIcon" />
                                        {!isCollapsed && "Products"}
                                    </li>
                                </Link>
                            )}
                            {(superadmin || marketing) && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "black",
                                        }}
                                        to="/newproduct"
                                        className="link"
                                    >
                                        <li className="sidebarListItem">
                                            <Add className="sidebarIcon" />
                                            {!isCollapsed && "New Product"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>
                    
                    {/* Logout Button */}
                    <div className="logoutSection">
                        <button className="logoutButton" onClick={handleLogout}>
                            <ExitToApp className="sidebarIcon" />
                            {!isCollapsed && "Logout"}
                        </button>
                    </div>
                </div>
            </div>
        )
    );
}
